// 案件一覧（ホーム）
//
// Claude Design プロトタイプ (_management/designs/ui_prototype/app/ProjectList.jsx) の
// デザインを再現。検索 + ソート + 更新日グルーピング + リッチな案件カード（案件別ビュー）。
// DEC-051 / S-053-B4: 「相手先別」ビューを追加。プロジェクトをセクションヘッダーに、
// その中に相手先（取引先）ごとの案件カードを並べる。カードタップで相手先タイムラインへ。
// 社名は議事録生成時に CF が自動抽出・マスタ突合して meeting.companyIds に付与される。
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { collection, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, CountBadge, Icon, Pill } from '../components/primitives';
import { Company, UNCLASSIFIED_ID, useCompanies } from '../useCompanies';
import { useUnresolvedCounts } from '../useUnresolvedCounts';
import { recordBadgeTap, recordEmptyRate } from '../lensMetrics';

interface DashboardData {
  summaryText?: string;
  stale?: boolean;
  computedFromMeetingCount?: number;
}

interface Project {
  id: string;
  name: string;
  description?: string;
  stage?: string;
  updatedAt?: any;
  dashboard?: DashboardData;
  meetingCount?: number;
  openActions?: number;
  alerts?: number;
}

interface Props {
  user: User;
  onSelectProject: (project: Project) => void;
  onSelectCompany: (project: Project, company: Company) => void;
  // 最上位タブ（会社別 / プロジェクト別）から表示モードを固定する場合に指定する。
  // 指定時は内部のセグメントコントロールを隠し、上位タブに切替を委ねる（S-056 新IA）。
  forcedViewMode?: ViewMode;
  // S-061-6: 建築レンズ × 有料プランのとき、プロジェクト別カードに未完アクション件数バッジを出す。
  // 営業・未設定・無料には渡さない（無風＋有料ゲート＋read増ゼロ）。
  showUnresolvedBadges?: boolean;
}

type SortKey = 'updated' | 'name' | 'fresh';
type ViewMode = 'project' | 'company';

// 集計 effect が作る生グループ（companyId のみ・名前解決しない）。
// マスタ（companyMap）更新で再集計が走らないよう、集計は companyId 文字列だけで行う。
interface RawGroup {
  companyId: string; // UNCLASSIFIED_ID の場合は仮想「未分類」
  count: number;
  lastAt: Date | null;
}
interface RawProjectGroups {
  project: Project;
  groups: RawGroup[];
}

// 描画用に名前解決した 1 グループ。
interface CompanyGroup {
  company: Company; // UNCLASSIFIED_ID の場合は仮想「未分類」
  count: number;
  lastAt: Date | null;
}
interface ProjectGroups {
  project: Project;
  groups: CompanyGroup[];
}

const toDate = (ts: any): Date | null => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatRelative = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === y.toDateString();
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return `今日 ${hhmm}`;
  if (isYesterday) return `昨日 ${hhmm}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${hhmm}`;
};

const formatDateOnly = (d: Date | null): string => {
  if (!d) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return '今日';
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return '昨日';
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const updatedGroup = (ts: any): 'today' | 'yesterday' | 'earlier' => {
  const d = toDate(ts);
  if (!d) return 'earlier';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'today';
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'yesterday';
  return 'earlier';
};

const ProjectCard: React.FC<{ p: Project; onOpen: (p: Project) => void; unresolvedCount?: number }> = ({ p, onOpen, unresolvedCount }) => {
  const summary = p.dashboard?.summaryText || p.description;
  const stale = p.dashboard?.stale === true;
  const updated = formatRelative(p.updatedAt);
  const meetingCount = p.meetingCount ?? p.dashboard?.computedFromMeetingCount;
  return (
    <Card hoverable onClick={() => onOpen(p)} pad={20} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            {p.stage && <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{p.stage}</span>}
            {/* S-061-6: 未完アクション件数バッジ（件数のみ・スコアなし=DEC-009）。0件は出さない。 */}
            {unresolvedCount != null && unresolvedCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                borderRadius: 'var(--r-1)', background: 'var(--cta-fill)',
                color: 'var(--amber-400)', font: 'var(--type-caption)', fontWeight: 600,
              }}>
                <Icon name="circle-dot" size={11} />未完{unresolvedCount}
              </span>
            )}
            {stale && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px',
                borderRadius: 'var(--r-1)', background: 'var(--accent-fill)',
                color: 'var(--teal-300)', font: 'var(--type-caption)', fontWeight: 500,
              }}>
                <Icon name="sparkle" size={11} />新着あり
              </span>
            )}
          </div>
          <div style={{ font: '700 17px/1.35 var(--font-sans)', color: 'var(--fg)' }}>{p.name}</div>
        </div>
        <Icon name="chevron-right" size={18} style={{ color: 'var(--fg-tertiary)', marginTop: 2 }} />
      </div>

      {summary && (
        <p style={{
          font: 'var(--type-body)', color: 'var(--fg-secondary)', fontSize: 13, lineHeight: 1.55,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {summary}
        </p>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        paddingTop: 12, borderTop: '1px solid var(--border)',
      }}>
        {updated && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            <Icon name="clock" size={13} />{updated}
          </span>
        )}
        {meetingCount != null && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            <Icon name="mic" size={13} />{meetingCount}件
          </span>
        )}
        <div style={{ flex: 1 }} />
        <CountBadge count={p.openActions} kind="amber" icon="circle-dot" />
        <CountBadge count={p.alerts} kind="teal" icon="git-compare" />
      </div>
    </Card>
  );
};

// 相手先カード（プロトタイプ案件カード踏襲のコンパクト版）。
const CompanyCard: React.FC<{ g: CompanyGroup; onOpen: () => void }> = ({ g, onOpen }) => {
  const isUnclassified = g.company.id === UNCLASSIFIED_ID;
  return (
    <Card hoverable onClick={onOpen} pad={16} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'grid', placeItems: 'center',
          background: isUnclassified ? 'var(--overlay-soft)' : 'var(--accent-fill)',
        }}>
          <Icon name={isUnclassified ? 'folder-search' : 'building'} size={15}
            style={{ color: isUnclassified ? 'var(--fg-tertiary)' : 'var(--teal-300)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            font: '600 15px/1.3 var(--font-sans)', color: isUnclassified ? 'var(--fg-secondary)' : 'var(--fg)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {isUnclassified ? '未分類' : g.company.name}
          </div>
        </div>
        <Icon name="chevron-right" size={16} style={{ color: 'var(--fg-tertiary)', flexShrink: 0 }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
          <Icon name="mic" size={12} />{g.count}件
        </span>
        {g.lastAt && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            <Icon name="clock" size={12} />{formatDateOnly(g.lastAt)}
          </span>
        )}
        {!isUnclassified && g.company.kind && <><div style={{ flex: 1 }} /><Pill kind="neutral">{g.company.kind}</Pill></>}
      </div>
    </Card>
  );
};

const ProjectListPage: React.FC<Props> = ({ user, onSelectProject, onSelectCompany, forcedViewMode, showUnresolvedBadges }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('updated');
  const [q, setQ] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(forcedViewMode ?? 'company');
  const [companyFilter, setCompanyFilter] = useState<string | null>(null);

  // 上位タブから表示モードが固定されたら追従する。
  useEffect(() => {
    if (forcedViewMode) { setViewMode(forcedViewMode); setCompanyFilter(null); }
  }, [forcedViewMode]);

  // 相手先別ビュー用: プロジェクト → 相手先グループ（生・companyId のみ）
  const [projectGroups, setProjectGroups] = useState<RawProjectGroups[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const { companies } = useCompanies(user.uid);

  useEffect(() => {
    const queryRef = query(
      collection(db, 'projects'),
      where('members', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      queryRef,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Project));
        setProjects(data);
        setError(null);
        setLoading(false);
      },
      (e: any) => {
        console.error('[ProjectListPage] failed to subscribe projects:', e);
        const detail = e?.code ? `${e.code}: ${e.message ?? e}` : (e?.message ?? String(e));
        setError(`プロジェクトの取得に失敗しました (${detail})`);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [user.uid]);

  const companyMap = useMemo(
    () => Object.fromEntries(companies.map((c) => [c.id, c])) as Record<string, Company>,
    [companies]
  );

  // S-061-6: 未完アクション件数バッジ用の件数源。建築レンズ×有料のときだけ購読する。
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const unresolvedCounts = useUnresolvedCounts(projectIds, !!showUnresolvedBadges);

  // 計測（empty_rate）: 全プロジェクトの件数が揃ったら、母数と空件数（未完0）を1回記録する。
  const lastEmptyRateKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showUnresolvedBadges || projectIds.length === 0) return;
    const allCounted = projectIds.every((id) => unresolvedCounts[id] != null);
    if (!allCounted) return;
    const empty = projectIds.reduce((acc, id) => acc + (unresolvedCounts[id] === 0 ? 1 : 0), 0);
    const key = `${projectIds.length}:${empty}`;
    if (lastEmptyRateKeyRef.current === key) return; // 同じ母数/空件数では二重記録しない
    lastEmptyRateKeyRef.current = key;
    recordEmptyRate(user.uid, projectIds.length, empty);
  }, [showUnresolvedBadges, projectIds, unresolvedCounts, user.uid]);

  // バッジ経由のプロジェクト遷移（badge_tap 計測を挟む）。
  const openProject = (project: Project) => {
    if (showUnresolvedBadges && (unresolvedCounts[project.id] ?? 0) > 0) {
      recordBadgeTap(user.uid, project.id);
    }
    onSelectProject(project);
  };

  // 相手先別ビュー: 各プロジェクトの meetings を取得し companyId ごとに集計する。
  // 件数を抑えるため getDocs（一括取得）で、viewMode が company のときだけ実行。
  // companyMap には依存しない（マスタ更新＝1タップ訂正等のたびに全件再取得しないため）。
  // 名前解決は描画直前の filteredProjectGroups（useMemo）で行う。
  useEffect(() => {
    if (viewMode !== 'company' || projects.length === 0) return;
    let cancelled = false;
    setGroupsLoading(true);
    (async () => {
      const result: RawProjectGroups[] = [];
      for (const p of projects) {
        try {
          const snap = await getDocs(collection(db, 'projects', p.id, 'meetings'));
          const acc = new Map<string, { count: number; lastAt: Date | null }>();
          snap.docs.forEach((d) => {
            const m = d.data() as any;
            if (m.deletedAt != null) return;
            const rec = toDate(m.recordedAt);
            const ids: string[] = Array.isArray(m.companyIds) && m.companyIds.length > 0
              ? m.companyIds : [UNCLASSIFIED_ID];
            ids.forEach((cid) => {
              const cur = acc.get(cid) || { count: 0, lastAt: null };
              cur.count += 1;
              if (rec && (!cur.lastAt || rec > cur.lastAt)) cur.lastAt = rec;
              acc.set(cid, cur);
            });
          });
          const groups: RawGroup[] = Array.from(acc.entries()).map(([cid, v]) => ({
            companyId: cid,
            count: v.count,
            lastAt: v.lastAt,
          }));
          // 件数降順、未分類は末尾
          groups.sort((a, b) => {
            if (a.companyId === UNCLASSIFIED_ID) return 1;
            if (b.companyId === UNCLASSIFIED_ID) return -1;
            return b.count - a.count;
          });
          if (groups.length > 0) result.push({ project: p, groups });
        } catch (e) {
          console.error('[ProjectListPage] failed to load meetings for grouping:', p.id, e);
        }
      }
      if (!cancelled) {
        setProjectGroups(result);
        setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, projects]);

  const sortOpts: { id: SortKey; label: string }[] = [
    { id: 'updated', label: '最終更新' },
    { id: 'name', label: '名前' },
    { id: 'fresh', label: '新着' },
  ];

  const list = useMemo(() => {
    let l = projects.filter((p) => !q || (p.name || '').includes(q) || (p.description || '').includes(q));
    l = [...l].sort((a, b) => {
      if (sort === 'name') return (a.name || '').localeCompare(b.name || '', 'ja');
      if (sort === 'fresh') return (b.dashboard?.stale === true ? 1 : 0) - (a.dashboard?.stale === true ? 1 : 0);
      return 0; // updated は購読順（updatedAt desc）
    });
    return l;
  }, [projects, q, sort]);

  const groups = sort === 'updated'
    ? [
        { key: 'today' as const, label: '今日' },
        { key: 'yesterday' as const, label: '昨日' },
        { key: 'earlier' as const, label: 'これまで' },
      ]
    : [{ key: null, label: null }];

  // 相手先別ビューの表示用フィルタ（検索 + 社名フィルタ）＋名前解決。
  // companyMap はここでのみ参照する（集計 effect は再実行しない）。
  const filteredProjectGroups: ProjectGroups[] = useMemo(() => {
    const resolve = (cid: string): Company =>
      cid === UNCLASSIFIED_ID
        ? { id: UNCLASSIFIED_ID, name: '未分類' }
        : (companyMap[cid] || { id: cid, name: '（不明な相手先）' });
    return projectGroups
      .filter((pg) => !q || (pg.project.name || '').includes(q))
      .map((pg) => ({
        project: pg.project,
        groups: pg.groups
          .filter((g) => !companyFilter || g.companyId === companyFilter)
          .map((g) => ({ company: resolve(g.companyId), count: g.count, lastAt: g.lastAt })),
      }))
      .filter((pg) => pg.groups.length > 0);
  }, [projectGroups, q, companyFilter, companyMap]);

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: '32px 24px 80px' }}>
      {/* ヘッダ: 大見出し「案件」＋ビューごとのサブタイトル / 右にメイン切替（セグメントコントロール） */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ font: 'var(--type-h1)', color: 'var(--fg)', marginBottom: 4 }}>案件</h1>
          <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            {viewMode === 'company'
              ? 'プロジェクト内の会社ごとに録音した会議が時系列で束ねられます。'
              : 'プロジェクトごとに録音した会議が時系列で束ねられます'}
          </p>
        </div>
        {/* メイン切替セグメント（会社別｜プロジェクト別）。選択中を teal 塗りでハイライト。 */}
        {/* 上位タブで固定されている場合は二重操作を避けるため隠す（S-056 新IA）。 */}
        {!forcedViewMode && (
        <div role="tablist" aria-label="案件の表示切替" style={{
          display: 'inline-flex', flexShrink: 0, padding: 3, gap: 3,
          borderRadius: 'var(--r-pill)', background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
        }}>
          {([{ id: 'company', label: '会社別' }, { id: 'project', label: 'プロジェクト別' }] as { id: ViewMode; label: string }[]).map((o) => {
            const active = viewMode === o.id;
            return (
              <button key={o.id} role="tab" aria-selected={active} onClick={() => { setViewMode(o.id); setCompanyFilter(null); }} style={{
                padding: '6px 14px', borderRadius: 'var(--r-pill)', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                background: active ? 'var(--accent)' : 'transparent',
                color: active ? 'var(--bg)' : 'var(--fg-secondary)',
                font: 'var(--type-caption)', fontWeight: 600,
                transition: 'background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease)',
              }}>{o.label}</button>
            );
          })}
        </div>
        )}
      </div>

      {/* 検索 ＋ ソート（プロジェクト別のみ） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0, maxWidth: 360 }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-tertiary)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="案件名で検索"
            style={{
              width: '100%', padding: '10px 14px 10px 36px', borderRadius: 'var(--r-2)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
              color: 'var(--fg)', font: 'var(--type-ui)', outline: 'none', fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
            }} />
        </div>

        {/* ソート（プロジェクト別のみ） */}
        {viewMode === 'project' && (
          <>
            <Icon name="arrow-up-down" size={14} style={{ color: 'var(--fg-tertiary)', flexShrink: 0 }} />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {sortOpts.map((o) => (
                <button key={o.id} onClick={() => setSort(o.id)} style={{
                  padding: '6px 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                  background: sort === o.id ? 'var(--accent-fill)' : 'transparent',
                  border: '1px solid ' + (sort === o.id ? 'var(--border-accent)' : 'var(--border-strong)'),
                  color: sort === o.id ? 'var(--accent)' : 'var(--fg-secondary)',
                  font: 'var(--type-caption)', fontWeight: 500, whiteSpace: 'nowrap',
                }}>{o.label}</button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 社名フィルタ（相手先別ビューでマスタがあるとき） */}
      {viewMode === 'company' && companies.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <Icon name="filter" size={13} style={{ color: 'var(--fg-tertiary)' }} />
          <button onClick={() => setCompanyFilter(null)} style={{
            padding: '5px 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
            background: companyFilter === null ? 'var(--accent-fill)' : 'transparent',
            border: '1px solid ' + (companyFilter === null ? 'var(--border-accent)' : 'var(--border-strong)'),
            color: companyFilter === null ? 'var(--accent)' : 'var(--fg-secondary)',
            font: 'var(--type-caption)', fontWeight: 500,
          }}>すべて</button>
          {companies.map((c) => (
            <button key={c.id} onClick={() => setCompanyFilter(c.id === companyFilter ? null : c.id)} style={{
              padding: '5px 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
              background: companyFilter === c.id ? 'var(--accent-fill)' : 'transparent',
              border: '1px solid ' + (companyFilter === c.id ? 'var(--border-accent)' : 'var(--border-strong)'),
              color: companyFilter === c.id ? 'var(--accent)' : 'var(--fg-secondary)',
              font: 'var(--type-caption)', fontWeight: 500,
            }}>{c.name}</button>
          ))}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>読み込み中...</div>}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--r-2)', background: 'var(--danger-fill)', border: '1px solid var(--danger-line)', color: 'var(--danger)', font: 'var(--type-caption)' }}>
          {error}
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--fg-tertiary)' }}>
          <Icon name="folder-search" size={32} style={{ marginBottom: 12 }} />
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 4 }}>プロジェクトがありません</p>
          <p style={{ font: 'var(--type-caption)' }}>Androidアプリからプロジェクトを作成してください</p>
        </div>
      )}

      {/* 案件別ビュー: 更新日グルーピング */}
      {viewMode === 'project' && !loading && !error && projects.length > 0 && groups.map((g) => {
        const items = g.key ? list.filter((p) => updatedGroup(p.updatedAt) === g.key) : list;
        if (!items.length) return null;
        return (
          <div key={g.label || 'all'} style={{ marginBottom: 28 }}>
            {g.label && (
              <div style={{
                font: 'var(--type-caption)', color: 'var(--fg-tertiary)', fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 2,
              }}>
                {g.label}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
              {items.map((p) => (
                <ProjectCard key={p.id} p={p} onOpen={openProject}
                  unresolvedCount={showUnresolvedBadges ? unresolvedCounts[p.id] : undefined} />
              ))}
            </div>
          </div>
        );
      })}

      {viewMode === 'project' && !loading && !error && projects.length > 0 && list.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--fg-tertiary)' }}>
          <Icon name="folder-search" size={28} style={{ marginBottom: 10 }} />
          <p style={{ font: 'var(--type-body)' }}>該当する案件がありません。</p>
        </div>
      )}

      {/* 相手先別ビュー: プロジェクト＝セクションヘッダー、中に相手先カード */}
      {viewMode === 'company' && !loading && !error && projects.length > 0 && (
        <>
          {groupsLoading && (
            <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>相手先を集計中...</div>
          )}
          {!groupsLoading && filteredProjectGroups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--fg-tertiary)' }}>
              <Icon name="building" size={28} style={{ marginBottom: 10 }} />
              <p style={{ font: 'var(--type-body)' }}>該当する相手先がありません。</p>
            </div>
          )}
          {!groupsLoading && filteredProjectGroups.map((pg) => (
            <div key={pg.project.id} style={{ marginBottom: 28 }}>
              <button onClick={() => onSelectProject(pg.project)} style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingLeft: 2,
                background: 'none', border: 'none', cursor: 'pointer',
              }}>
                <Icon name="folder" size={15} style={{ color: 'var(--fg-tertiary)' }} />
                <span style={{ font: '700 15px/1 var(--font-sans)', color: 'var(--fg)' }}>{pg.project.name}</span>
                <Icon name="chevron-right" size={14} style={{ color: 'var(--fg-tertiary)' }} />
              </button>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {pg.groups.map((g) => (
                  <CompanyCard key={g.company.id} g={g} onOpen={() => onSelectCompany(pg.project, g.company)} />
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ProjectListPage;
